import React, {
  useState,
  cloneElement,
  useContext,
  useMemo,
  useRef,
  useEffect,
} from "react";

import {
  Tabs,
  Tab,
  TabHeaders,
  TabHeader,
  TabBadge,
  Loader,
  Notification,
  NotificationType,
} from "@transfr-inc/dashboard-components";

import { TemplatesTable } from "./templates.table";
import { UNIVERSAL_MENU_NAME } from "../../../../constants/template-types";
import { Category } from "../../../../constants/category.model";
import { OrgsModal } from "@src/components/modals/orgs/orgs.modal";
import { getRoleFeatures } from "../../../../constants/role";
import { SessionContext } from "../../../../context/session.context";
import TabErrorBoundary from "../../../../components/error-boundary/tab-error-boundary";
import { Button } from "@transfr-inc/dashboard-components/forms";

import "./templates.tabs.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const TAB_NAMES = {
  MENUS: "templates-menus",
  CATEGORIES: "templates-categories",
  PROGRAMS: "templates-programs",
};

const DATA_TYPES = {
  MENUS: "Menus",
  CATEGORIES: "Categories",
  PROGRAMS: "Programs",
};

const TemplateTab = ({
  name,
  data,
  dataType,
  responsiveStyles,
  actionMenuOptions,
  isMenuActionVisible,
  openMenuOrgsModal,
  openProgramOrgsModal,
  loading = false,
  error,
  onRetry,
  children,
}) => {
  if (loading) {
    return (
      <Tab name={name}>
        <div className="tab-loading-container">
          <Loader />
          <p>Loading {dataType}...</p>
        </div>
      </Tab>
    );
  }

  if (error) {
    return (
      <Tab name={name}>
        <div className="tab-error-boundary">
          <Notification
            type={NotificationType.error}
            icon={["fa-solid", "exclamation-triangle"]}
            closable={false}
          >
            <div className="error-content">
              <h4>Failed to load {dataType}</h4>
              <p>
                There was an error loading this section. This might be due to a
                network issue or server problem.
              </p>
              <div className="error-actions">
                <Button onClick={onRetry} primary>
                  Try Again
                </Button>
                <Button onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
              </div>
              {process.env.NODE_ENV === "development" && error && (
                <details className="error-details">
                  <summary>Error Details (Development)</summary>
                  <pre>{error.toString()}</pre>
                </details>
              )}
            </div>
          </Notification>
        </div>
      </Tab>
    );
  }

  return (
    <Tab name={name}>
      <TabErrorBoundary
        tabName={dataType}
        onRetry={onRetry}
        onRefresh={() => window.location.reload()}
      >
        <TemplatesTable
          className="templates-table"
          data={data || []}
          dataType={dataType}
          responsiveStyles={responsiveStyles}
          actionMenuOptions={actionMenuOptions}
          isMenuActionVisible={isMenuActionVisible}
          openMenuOrgsModal={openMenuOrgsModal}
          openProgramOrgsModal={openProgramOrgsModal}
        />
      </TabErrorBoundary>
      {children}
    </Tab>
  );
};

export const TemplatesTabs = ({
  data,
  responsiveStyles,
  actionMenuOptions,
  tabStates = {},
  onRetryTab,
  activeTab,
  onTabChange,
}) => {
  const { menuData, categoryData, programData } = data;
  const {
    menu: menuActions,
    category: categoryActions,
    program: programActions,
  } = actionMenuOptions;
  const [selectedMenu4Orgs, setSelectedMenu4Orgs] = useState();
  const [selectedProgramOrgs, setSelectedProgramOrgs] = useState();
  const isProgramsEnabled =
    process.env.PROGRAMS_ENABLED.toLowerCase() === "true";
  const { role } = useContext(SessionContext);

  const getTabState = (tabName) =>
    tabStates[tabName] || { loading: false, error: undefined, data: [] };

  const { loading: menuLoading, error: menuError } = getTabState("menus");
  const { loading: categoryLoading, error: categoryError } =
    getTabState("categories");
  const { loading: programLoading, error: programError } =
    getTabState("programs");

  const createTabHeader = (name, displayName, data, { loading, error }) => {
    const { length: dataLength = 0 } = data || {};

    return (
      <TabHeader name={name} className={`${name}-tab-header`}>
        <div className="tab-header-content">
          <div className="tab-header-text">{displayName}</div>
          <TabBadge count={dataLength} />
          {loading && <Loader className="btn-loader" />}
          {error && (
            <FontAwesomeIcon
              icon={["fa-solid", "exclamation-triangle"]}
              className="text-warning"
            />
          )}
        </div>
      </TabHeader>
    );
  };

  const tabConfigs = useMemo(() => {
    const configs = [
      {
        name: TAB_NAMES.MENUS,
        displayName: DATA_TYPES.MENUS,
        data: menuData,
        state: { loading: menuLoading, error: menuError },
        actionMenuOptions: [menuActions.delete, menuActions.edit],
        isMenuActionVisible: (menu, actionMenuOptions) =>
          menu.name !== UNIVERSAL_MENU_NAME ? actionMenuOptions : [],
        openMenuOrgsModal: setSelectedMenu4Orgs,
        onRetry: () => onRetryTab?.("menus"),
        children: selectedMenu4Orgs && (
          <OrgsModal
            open={!!selectedMenu4Orgs}
            entity={selectedMenu4Orgs}
            onClose={() => setSelectedMenu4Orgs()}
            breadcrumbLabel="All Menus"
          />
        ),
      },
      {
        name: TAB_NAMES.CATEGORIES,
        displayName: DATA_TYPES.CATEGORIES,
        data: categoryData,
        state: { loading: categoryLoading, error: categoryError },
        actionMenuOptions: [categoryActions.delete, categoryActions.edit],
        isMenuActionVisible: (category, actionMenuOptions) =>
          category?.categoryType === Category.INTERNAL_CUSTOM
            ? actionMenuOptions
            : [],
        onRetry: () => onRetryTab?.("categories"),
      },
    ];

    if (isProgramsEnabled) {
      configs.push({
        name: TAB_NAMES.PROGRAMS,
        displayName: DATA_TYPES.PROGRAMS,
        data: programData,
        state: { loading: programLoading, error: programError },
        actionMenuOptions: [
          ...(getRoleFeatures(role)?.canDeleteProgramCourse
            ? [programActions.delete]
            : []),
        ],
        isMenuActionVisible: (program, actionMenuOptions) => actionMenuOptions,
        openProgramOrgsModal: setSelectedProgramOrgs,
        children: selectedProgramOrgs && (
          <OrgsModal
            open={!!selectedProgramOrgs}
            entity={selectedProgramOrgs}
            onClose={() => setSelectedProgramOrgs()}
            breadcrumbLabel="All Programs"
          />
        ),
        onRetry: () => onRetryTab?.("programs"),
      });
    }

    return configs;
  }, [
    menuData,
    categoryData,
    programData,
    menuLoading,
    menuError,
    categoryLoading,
    categoryError,
    programLoading,
    programError,
    menuActions,
    categoryActions,
    programActions,
    selectedMenu4Orgs,
    selectedProgramOrgs,
    isProgramsEnabled,
    role,
    onRetryTab,
  ]);

  const tabs = useMemo(
    () =>
      tabConfigs.map((config) => ({
        name: config.name,
        header: createTabHeader(
          config.name,
          config.displayName,
          config.data,
          config.state
        ),
        content: (
          <TemplateTab
            name={config.name}
            data={config.data}
            dataType={config.displayName}
            responsiveStyles={responsiveStyles}
            actionMenuOptions={config.actionMenuOptions}
            isMenuActionVisible={config.isMenuActionVisible}
            openMenuOrgsModal={config.openMenuOrgsModal}
            openProgramOrgsModal={config.openProgramOrgsModal}
            loading={config.state.loading}
            error={config.state.error}
            onRetry={config.onRetry}
          >
            {config.children}
          </TemplateTab>
        ),
      })),
    [tabConfigs, responsiveStyles]
  );

  const validTabs = tabs.filter(
    (tab) => tab && tab.name && tab.header && tab.content
  );

  const selectedTabName = activeTab || validTabs[0]?.name;

  const handleTabChange = (tabName) => {
    if (onTabChange) {
      onTabChange(tabName);
    }
  };

  return (
    <Tabs className="templates-tabs" selectedTabName={selectedTabName}>
      <TabHeaders onSelectedTabChange={handleTabChange}>
        {validTabs.map(({ header, name }, i) =>
          cloneElement(header, { key: `tab-header-${name}-${i}` })
        )}
      </TabHeaders>
      {validTabs.map(({ content, name }, i) =>
        cloneElement(content, { key: `tab-content-${name}-${i}` })
      )}
    </Tabs>
  );
};
